import { useWaitFor } from '@ifixit/ui/hooks/use-wait-for';
import { useEffect } from 'react';
import { hasUserConsented } from './consent';

// https://shopify.dev/docs/api/customer-privacy#installation-on-a-custom-storefront
export type ShopifyAPI = {
   customerPrivacy: {
      currentVisitorConsent(): {
         marketing: '' | 'yes' | 'no';
         analytics: '' | 'yes' | 'no';
         preferences: '' | 'yes' | 'no';
         sale_of_data: '' | 'yes' | 'no';
      };
      getTrackingConsentMetafield(key: string): string | null;
      setTrackingConsent(
         props: {
            marketing?: boolean;
            analytics?: boolean;
            preferences?: boolean;
            sale_of_data?: boolean;
            headlessStorefront: boolean;
            checkoutRootDomain: string;
            storefrontRootDomain: string;
            storefrontAccessToken: string;
            metafields?: { key: string; value: string | null }[];
         },
         callback?: () => unknown
      ): void;
   };
};

export type ShopifyConfig = { checkout: string; storefront: string; accessToken: string };

declare global {
   interface Window {
      Shopify?: ShopifyAPI;
   }
}

interface ShopifyConsentProps {
   piwikSiteId: string;
   shopifyConfig: ShopifyConfig;
   needsConsent: boolean;
}

/**
 * Syncs consent state to Shopify when the API is available and parameters change.
 * Safe to call multiple times - will only update if consent state differs.
 */
export function useSyncShopifyConsent({
   piwikSiteId,
   shopifyConfig,
   needsConsent,
}: ShopifyConsentProps) {
   const shopifyReady = useWaitFor(() => typeof window !== 'undefined' && !!window.Shopify);
   const { checkout, storefront, accessToken } = shopifyConfig;
   useEffect(() => {
      if (!shopifyReady) return;

      const userConsent = hasUserConsented(piwikSiteId) ?? !needsConsent;
      if (!shopifyConsentMatches(userConsent))
         updateShopifyConsent(userConsent, { checkout, storefront, accessToken });
   }, [shopifyReady, piwikSiteId, checkout, storefront, accessToken, needsConsent]);
}

export function updateShopifyConsent(accepted: boolean, shopifyConfig: ShopifyConfig) {
   window.Shopify?.customerPrivacy.setTrackingConsent({
      'marketing': accepted,
      'analytics': accepted,
      'preferences': accepted,
      'sale_of_data': accepted,
      'headlessStorefront': true,
      'checkoutRootDomain': shopifyConfig.checkout,
      'storefrontRootDomain': shopifyConfig.storefront,
      'storefrontAccessToken': shopifyConfig.accessToken,
   });
}

function shopifyConsentMatches(userConsent: boolean) {
   const currentVisitorConsent = window.Shopify?.customerPrivacy.currentVisitorConsent();
   if (!currentVisitorConsent) return false;

   const consentChoice = userConsent ? 'yes' : 'no';
   const { analytics, marketing, preferences, sale_of_data } = currentVisitorConsent;
   return (
      analytics === consentChoice &&
      marketing === consentChoice &&
      preferences === consentChoice &&
      sale_of_data === consentChoice
   );
}
